import React, { useEffect, useMemo, useState, Component } from 'react';
import { Shield, FileText, Database, Eye, Download, Filter, Award } from 'lucide-react';
import { getAllQuestions, QUESTION_DATA_VERSION } from '../../../data/questionLoader';
import { MODULES } from '../../../data/modules';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { GovernanceReport } from './GovernanceReport';
import { ContentQuality } from './ContentQuality';
import { SeedAudit } from './SeedAudit';
import { ValidationSuite } from './ValidationSuite';
import { QuestionQualityPanel } from './QuestionQualityPanel';
import { DidacticDepthPanel } from './DidacticDepthPanel';
import { buildQualityQuestionDataset } from './qualityQuestionDataset';
import { buildQualityReport } from '../../../governance/questionQualityEngine';
import { QualityTab, QualityWorkspaceProvider, useQualityWorkspaceState } from './useQualityWorkspaceState';
import { exportQualityData, QualityExportFormat } from './qualityExport';
const TABS: Array<{
  id: QualityTab;
  label: string;
  shortLabel: string;
  icon: ComponentType<{
    className?: string;
  }>;
  color: string;
}> = [{
  id: 'governance',
  label: 'Governance',
  shortLabel: 'Gov',
  icon: Shield,
  color: 'text-purple-600'
}, {
  id: 'quality',
  label: 'Question Quality',
  shortLabel: 'Quality',
  icon: Award,
  color: 'text-amber-500'
}, {
  id: 'didactic-depth',
  label: 'Didactic Depth',
  shortLabel: 'Depth',
  icon: Award,
  color: 'text-indigo-600'
}, {
  id: 'content',
  label: 'Content',
  shortLabel: 'Content',
  icon: FileText,
  color: 'text-blue-600'
}, {
  id: 'seeds',
  label: 'Seeds',
  shortLabel: 'Seeds',
  icon: Database,
  color: 'text-emerald-600'
}, {
  id: 'visuals',
  label: 'Visuals',
  shortLabel: 'Visuals',
  icon: Eye,
  color: 'text-indigo-600'
}];
export function QualityWorkspace({
  refreshKey = 0,
  initialTab
}: {
  refreshKey?: number;
  initialTab?: QualityTab;
}) {
  const records = useMemo(() => {
    const questions = getAllQuestions();
    return buildQualityQuestionDataset(questions);
  }, [refreshKey, QUESTION_DATA_VERSION]);
  // NEW-BUG-2 fix: compute qualityReport once here, pass down to avoid
  // QuestionQualityPanel recomputing it independently (was a 3rd validation pass)
  const qualityReport = useMemo(() => {
    const questions = records.map(r => r.question);
    return buildQualityReport(questions);
  }, [records]);
  return <QualityWorkspaceProvider records={records} data-id="element-2427">
      <WorkspaceInner refreshKey={refreshKey} qualityReport={qualityReport} initialTab={initialTab} data-id="element-2428" />
    </QualityWorkspaceProvider>;
}
function WorkspaceInner({
  refreshKey = 0,
  qualityReport,
  initialTab
}: {
  refreshKey?: number;
  qualityReport: ReturnType<typeof buildQualityReport>;
  initialTab?: QualityTab;
}) {
  const {
    records,
    filteredRecords,
    activeTab,
    setActiveTab,
    selectedModule,
    setSelectedModule,
    selectedTier,
    setSelectedTier,
    selectedGroup,
    setSelectedGroup,
    search,
    setSearch,
    severity,
    setSeverity
  } = useQualityWorkspaceState();
  const [format, setFormat] = useState<QualityExportFormat>('csv');
  const [showFilters, setShowFilters] = useState(false);
  // Handle initial tab prop
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, setActiveTab]);
  // Keep event listener as fallback for now, but prefer prop-based navigation
  useEffect(() => {
    const onSetTab = (event: Event) => {
      const customEvent = event as CustomEvent<QualityTab>;
      if (customEvent.detail) setActiveTab(customEvent.detail);
    };
    window.addEventListener('quality-workspace:set-tab', onSetTab);
    return () => window.removeEventListener('quality-workspace:set-tab', onSetTab);
  }, [setActiveTab]);
  const totals = useMemo(() => {
    const allIssues = records.flatMap(r => r.issues);
    const qualityScores = records.map(r => r.qualityScore).filter((s): s is number => s !== undefined);
    const avgQuality = qualityScores.length > 0 ? Math.round(qualityScores.reduce((a, b) => a + b, 0) / qualityScores.length) : 0;
    const genericFeedbackCount = records.filter(r => r.qualityFlags?.some(f => f.label === 'Generic feedback')).length;
    const poorQualityCount = records.filter(r => (r.qualityScore ?? 100) < 40).length;
    return {
      questions: records.length,
      issues: allIssues.length,
      critical: allIssues.filter(i => i.severity === 'critical').length,
      warnings: allIssues.filter(i => i.severity === 'warning').length,
      info: allIssues.filter(i => i.severity === 'info').length,
      avgQuality,
      genericFeedbackCount,
      poorQualityCount
    };
  }, [records]);
  const filteredTotals = useMemo(() => {
    const tabIssues = filteredRecords.flatMap(r => r.issues.filter(i => i.category === activeTab));
    return {
      questions: filteredRecords.length,
      issues: tabIssues.length,
      critical: tabIssues.filter(i => i.severity === 'critical').length,
      warnings: tabIssues.filter(i => i.severity === 'warning').length
    };
  }, [filteredRecords, activeTab]);
  const exportRows = filteredRecords.flatMap(record => record.issues.filter(issue => issue.category === activeTab).map(issue => ({
    questionId: record.question.id,
    module: record.question.module,
    tier: record.question.tier,
    group: record.question.group,
    category: issue.category,
    severity: issue.severity,
    message: issue.message
  })));
  const tiers = Array.from(new Set(records.map(r => r.question.tier))).sort((a, b) => a - b);
  const activeFiltersCount = [selectedModule !== 'all', selectedTier !== 'all', selectedGroup !== 'all', search.trim() !== '', severity !== 'all'].filter(Boolean).length;
  return <div className="space-y-6" data-id="element-2429">
      {/* Header KPIs */}
      <Card className="p-4 sm:p-5" data-id="element-2430">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4" data-id="element-2431">
          <div className="flex-1 min-w-0" data-id="element-2432">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 truncate" data-id="element-2433">
              Quality Workspace
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5" data-id="element-2434">
              {records.length} questions analyzed across all modules
            </p>
          </div>
          <Badge variant={totals.critical > 0 ? 'danger' : totals.warnings > 0 ? 'warning' : 'success'} size="lg" className="self-start sm:self-auto" data-id="element-2435">
            {totals.critical > 0 ? `${totals.critical} Critical` : totals.warnings > 0 ? `${totals.warnings} Warnings` : 'All Clear'}
          </Badge>
        </div>

        {/* Row 1: Issue counts */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-3" data-id="element-2436">
          <KpiCard label="Questions" value={totals.questions} color="slate" data-id="element-2437" />
          <KpiCard label="Total Issues" value={totals.issues} color="slate" data-id="element-2438" />
          <KpiCard label="Critical" value={totals.critical} color={totals.critical > 0 ? 'rose' : 'emerald'} data-id="element-2439" />
          <KpiCard label="Warnings" value={totals.warnings} color={totals.warnings > 0 ? 'amber' : 'emerald'} data-id="element-2440" />
          <KpiCard label="Info" value={totals.info} color="blue" data-id="element-2441" />
        </div>

        {/* Row 2: Quality-specific KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-slate-100" data-id="element-2442">
          <QualityKpiCard label="Avg Quality Score" value={totals.avgQuality} suffix="/ 100" color={totals.avgQuality >= 70 ? 'emerald' : totals.avgQuality >= 50 ? 'amber' : 'rose'} showBar data-id="element-2443" />
          <QualityKpiCard label="Generic Feedback" value={totals.genericFeedbackCount} suffix="questions" color={totals.genericFeedbackCount > 0 ? 'rose' : 'emerald'} description="Boilerplate explanations" data-id="element-2444" />
          <QualityKpiCard label="Poor Quality" value={totals.poorQualityCount} suffix="questions" color={totals.poorQualityCount > 0 ? 'rose' : 'emerald'} description="Score < 40 / 100" data-id="element-2445" />
        </div>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto scrollbar-hide" data-id="element-2446">
        {TABS.map(tab => {
        const Icon = tab.icon;
        const tabIssues = records.flatMap(r => r.issues.filter(i => i.category === tab.id));
        const tabCritical = tabIssues.filter(i => i.severity === 'critical').length;
        const isActive = activeTab === tab.id;
        return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-shrink-0 flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${isActive ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-2447">
              <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : tab.color}`} data-id="element-2448" />
              <span className="hidden sm:inline" data-id="element-2449">{tab.label}</span>
              <span className="inline sm:hidden" data-id="element-2450">{tab.shortLabel}</span>
              {tabCritical > 0 && <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isActive ? 'bg-rose-500 text-white' : 'bg-rose-100 text-rose-700'}`} data-id="element-2451">
                  {tabCritical}
                </span>}
            </button>;
      })}
      </div>

      {/* Filter Bar */}
      <Card className="p-4" data-id="element-2452">
        <div className="flex flex-col gap-3" data-id="element-2453">
          {/* Top row: Filter toggle and stats */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-2454">
            <button onClick={() => setShowFilters(v => !v)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors self-start" data-id="element-2455">
              <Filter className="w-4 h-4" data-id="element-2456" />
              Filters
              {activeFiltersCount > 0 && <Badge variant="primary" size="sm" data-id="element-2457">
                  {activeFiltersCount} active
                </Badge>}
            </button>

            {/* Export controls */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-id="element-2458">
              <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-2459">
                {filteredTotals.questions} questions · {filteredTotals.issues}{' '}
                issues
              </span>
              <div className="flex gap-2" data-id="element-2460">
                <select value={format} onChange={e => setFormat(e.target.value as QualityExportFormat)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300" data-id="element-2461">
                  <option value="csv" data-id="element-2462">CSV</option>
                  <option value="json" data-id="element-2463">JSON</option>
                  <option value="markdown" data-id="element-2464">Markdown</option>
                </select>
                <Button size="sm" variant="secondary" onClick={() => exportQualityData(`quality-${activeTab}`, format, exportRows)} className="gap-1.5" data-id="element-2465">
                  <Download className="w-3.5 h-3.5" data-id="element-2466" />
                  <span className="hidden sm:inline" data-id="element-2467">Export</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Filter controls */}
          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 pt-3 border-t border-slate-100" data-id="element-2468">
              <select value={selectedModule} onChange={e => setSelectedModule(e.target.value as any)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2469">
                <option value="all" data-id="element-2470">All modules</option>
                {MODULES.map(m => <option key={m.id} value={m.id} data-id="element-2471">
                    {m.id}
                  </option>)}
              </select>
              <select value={selectedTier} onChange={e => setSelectedTier(e.target.value === 'all' ? 'all' : Number(e.target.value))} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2472">
                <option value="all" data-id="element-2473">All tiers</option>
                {tiers.map(tier => <option key={tier} value={tier} data-id="element-2474">
                    Tier {tier}
                  </option>)}
              </select>
              <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value === 'all' ? 'all' : Number(e.target.value))} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2475">
                <option value="all" data-id="element-2476">All groups</option>
                {[6, 7, 8].map(g => <option key={g} value={g} data-id="element-2477">
                    Group {g}
                  </option>)}
              </select>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ID / family / text…" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full sm:col-span-2 lg:col-span-1" data-id="element-2478" />
              <select value={severity} onChange={e => setSeverity(e.target.value as any)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2479">
                <option value="all" data-id="element-2480">All severity</option>
                <option value="critical" data-id="element-2481">Critical</option>
                <option value="warning" data-id="element-2482">Warning</option>
                <option value="info" data-id="element-2483">Info</option>
              </select>
            </div>}
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === 'governance' && <GovernanceReport data-id="element-2484" />}
      {activeTab === 'quality' && <QuestionQualityPanel report={qualityReport} data-id="element-2485" />}
      {activeTab === 'didactic-depth' && <DidacticDepthPanel data-id="element-2486" />}
      {activeTab === 'content' && <ContentQuality data-id="element-2487" />}
      {activeTab === 'seeds' && <SeedAudit refreshKey={refreshKey} data-id="element-2488" />}
      {activeTab === 'visuals' && <ValidationSuite data-id="element-2489" />}
    </div>;
}
function KpiCard({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: 'slate' | 'rose' | 'amber' | 'emerald' | 'blue';
}) {
  const colors = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700'
  };
  return <div className={`rounded-xl border p-3 text-center ${colors[color]}`} data-id="element-2490">
      <div className="text-xl sm:text-2xl font-black" data-id="element-2491">{value}</div>
      <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider opacity-70 mt-0.5" data-id="element-2492">
        {label}
      </div>
    </div>;
}
function QualityKpiCard({
  label,
  value,
  suffix,
  color,
  showBar,
  description
}: {
  label: string;
  value: number;
  suffix: string;
  color: 'rose' | 'amber' | 'emerald';
  showBar?: boolean;
  description?: string;
}) {
  const colors = {
    rose: {
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      text: 'text-rose-700',
      bar: 'bg-rose-400'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      bar: 'bg-amber-400'
    },
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      bar: 'bg-emerald-400'
    }
  };
  const c = colors[color];
  return <div className={`rounded-xl border p-3 sm:p-4 ${c.bg} ${c.border}`} data-id="element-2493">
      <div className="flex items-baseline gap-1.5 mb-1" data-id="element-2494">
        <span className={`text-xl sm:text-2xl font-black ${c.text}`} data-id="element-2495">
          {value}
        </span>
        <span className={`text-xs font-medium opacity-60 ${c.text}`} data-id="element-2496">
          {suffix}
        </span>
      </div>
      <div className={`text-xs font-bold uppercase tracking-wider ${c.text} opacity-80`} data-id="element-2497">
        {label}
      </div>
      {description && <div className={`text-xs mt-0.5 ${c.text} opacity-60`} data-id="element-2498">
          {description}
        </div>}
      {showBar && <div className="mt-2 h-1.5 w-full bg-white/60 rounded-full overflow-hidden" data-id="element-2499">
          <div className={`h-full rounded-full ${c.bar} transition-all duration-500`} style={{
        width: `${value}%`
      }} data-id="element-2500" />
        </div>}
    </div>;
}